import React, { Component, createRef } from 'react';
import cx from 'classnames';
import { ImgWrap } from 'pubBiz';
import './FloatWindow.less';

const MOVE_THRESHOLD = 5;

export default class FloatWindow extends Component {
  constructor(props) {
    super(props);
    this.windowRef = createRef();
    this.pointerId = null;
    this.startX = 0;
    this.startY = 0;
    this.startLeft = 0;
    this.startTop = 0;
    this.currentLeft = 0;
    this.currentTop = 0;
    this.hasMoved = false;
    this.ignoreMouseUntil = 0;
    this.state = {
      left: null,
      top: null,
      isDragging: false
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    this.releasePointerCapture();
  }

  getValidLeft = (left) => {
    const element = this.windowRef.current;
    const width = element ? element.getBoundingClientRect().width : 0;
    const maxLeft = Math.max(0, window.innerWidth - width);
    return Math.min(Math.max(0, left), maxLeft);
  };

  getValidTop = (top) => {
    const element = this.windowRef.current;
    const height = element ? element.getBoundingClientRect().height : 0;
    const maxTop = Math.max(0, window.innerHeight - height);
    return Math.min(Math.max(0, top), maxTop);
  };

  getCurrentPosition = () => {
    const element = this.windowRef.current;
    if (!element) return { left: 0, top: 0 };
    const { left, top } = element.getBoundingClientRect();
    return { left, top };
  };

  handleResize = () => {
    if (!this.windowRef.current) return;
    this.setState(({ left, top }) => ({
      left: left === null ? null : this.getValidLeft(left),
      top: top === null ? null : this.getValidTop(top)
    }));
  };

  getTouchPoint = (event) => {
    const touch = event.touches[0] || event.changedTouches[0];
    return touch ? { clientX: touch.clientX, clientY: touch.clientY } : null;
  };

  startDrag = (clientX, clientY, pointerId, target) => {
    const { left, top } = this.getCurrentPosition();
    this.pointerId = pointerId;
    this.startX = clientX;
    this.startY = clientY;
    this.startLeft = left;
    this.startTop = top;
    this.currentLeft = left;
    this.currentTop = top;
    this.hasMoved = false;
    this.setState({
      left,
      top,
      isDragging: false
    });

    if (target && target.setPointerCapture && pointerId !== 'touch') {
      target.setPointerCapture(pointerId);
    }
  };

  moveDrag = (clientX, clientY, event) => {
    if (this.pointerId === null) return;

    const moveX = clientX - this.startX;
    const moveY = clientY - this.startY;
    if (!this.hasMoved && Math.max(Math.abs(moveX), Math.abs(moveY)) < MOVE_THRESHOLD) return;

    this.hasMoved = true;
    this.currentLeft = this.getValidLeft(this.startLeft + moveX);
    this.currentTop = this.getValidTop(this.startTop + moveY);
    event.preventDefault();
    this.setState({
      left: this.currentLeft,
      top: this.currentTop,
      isDragging: true
    });
  };

  handlePointerDown = (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    this.startDrag(event.clientX, event.clientY, event.pointerId, event.currentTarget);
  };

  handlePointerMove = (event) => {
    if (this.pointerId !== event.pointerId) return;
    this.moveDrag(event.clientX, event.clientY, event);
  };

  handleTouchStart = (event) => {
    const point = this.getTouchPoint(event);
    if (!point) return;
    this.startDrag(point.clientX, point.clientY, 'touch', event.currentTarget);
  };

  handleTouchMove = (event) => {
    const point = this.getTouchPoint(event);
    if (!point || this.pointerId !== 'touch') return;
    this.moveDrag(point.clientX, point.clientY, event);
  };

  handleMouseDown = (event) => {
    if (Date.now() < this.ignoreMouseUntil || event.button !== 0) return;
    this.startDrag(event.clientX, event.clientY, 'mouse', event.currentTarget);
  };

  handleMouseMove = (event) => {
    if (this.pointerId !== 'mouse') return;
    this.moveDrag(event.clientX, event.clientY, event);
  };

  finishPointer = (event, triggerClick) => {
    if (this.pointerId !== event.pointerId) return;
    this.finishDrag(triggerClick);
  };

  finishDrag = (triggerClick) => {
    const shouldClick = triggerClick && !this.hasMoved;
    const finalTop = this.getValidTop(this.currentTop);
    const wasTouch = this.pointerId === 'touch';
    this.releasePointerCapture();
    if (wasTouch) this.ignoreMouseUntil = Date.now() + 500;
    this.setState({
      left: null,
      top: this.hasMoved ? finalTop : null,
      isDragging: false
    });

    if (shouldClick && this.props.onClick) {
      this.props.onClick();
    }
  };

  handlePointerUp = (event) => {
    this.finishPointer(event, true);
  };

  handlePointerCancel = (event) => {
    this.finishPointer(event, false);
  };

  handleTouchEnd = () => {
    if (this.pointerId === 'touch') this.finishDrag(true);
  };

  handleMouseUp = () => {
    if (this.pointerId === 'mouse') this.finishDrag(true);
  };

  releasePointerCapture = () => {
    const element = this.windowRef.current;
    if (
      element &&
      this.pointerId !== null &&
      this.pointerId !== 'touch' &&
      this.pointerId !== 'mouse' &&
      element.releasePointerCapture
    ) {
      try {
        element.releasePointerCapture(this.pointerId);
      } catch (error) {
        // 指针捕获可能已由浏览器自动释放，此时无需额外处理。
      }
    }
    this.pointerId = null;
  };

  handleClosePointerDown = (event) => {
    event.stopPropagation();
  };

  handleClose = (event) => {
    event.stopPropagation();
    if (this.props.onClose) this.props.onClose();
  };

  render() {
    const { data, isHide } = this.props;
    if (!data || !data.imgData) return null;

    const { windowName = '' } = data;
    const { left, top, isDragging } = this.state;
    const style =
      left === null
        ? top === null
          ? undefined
          : { top: `${top}px`, bottom: 'auto' }
        : {
            left: `${left}px`,
            top: `${top}px`,
            right: 'auto',
            bottom: 'auto'
          };

    return (
      <div
        ref={this.windowRef}
        className={cx('shop-float-window', {
          'shop-float-window-hide': isHide,
          'shop-float-window-dragging': isDragging
        })}
        style={style}
        onPointerDown={this.handlePointerDown}
        onPointerMove={this.handlePointerMove}
        onPointerUp={this.handlePointerUp}
        onPointerCancel={this.handlePointerCancel}
        onTouchStart={this.handleTouchStart}
        onTouchMove={this.handleTouchMove}
        onTouchEnd={this.handleTouchEnd}
        onMouseDown={this.handleMouseDown}
        onMouseMove={this.handleMouseMove}
        onMouseUp={this.handleMouseUp}
        role="button"
        tabIndex={0}
        aria-label={windowName || '悬浮窗'}>
        <ImgWrap src={data.imgData.imgUrl} mode="full" w="100%" h="100%" />
        <div
          className="close-btn"
          onPointerDown={this.handleClosePointerDown}
          onTouchStart={this.handleClosePointerDown}
          onMouseDown={this.handleClosePointerDown}
          onClick={this.handleClose}>
          <span className="iconfont icon-reeor" />
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/pages/Shop/Home/ShopHome/components/FloatWindow/FloatWindow.js